import dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TrueTime } from '../../time/true-time.js';

export class KeyValueStore extends DataModel {
  static readonly TYPE_ID: string = 'ecf220a03d874f9fbba612d98ccba25a';

  private constructor(
    public readonly uniqueId: string,
    public readonly creationDate: dayjs.Dayjs,
    private readonly store: SerializedData,
  ) {
    super();
  }

  get $typeId(): string {
    return KeyValueStore.TYPE_ID;
  }

  static empty(uniqueId: string): KeyValueStore {
    return new KeyValueStore(uniqueId, TrueTime.now(), {});
  }

  static fromMap(context: SerializationContext, map: SerializedData): KeyValueStore {
    return new KeyValueStore(
        MapUtils.get<string>(map, 'uniqueId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<SerializedData>(map, 'store'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      'store': this.store,
    };
  }

  clone(): this {
    return new KeyValueStore(
        this.uniqueId,
        this.creationDate,
        { ...this.store },
    ) as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    creationDate = this.creationDate,
  } : {
    uniqueId?: string
    creationDate?: dayjs.Dayjs
  }): this {
    return new KeyValueStore(
        uniqueId,
        creationDate,
        { ...this.store },
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.store === other.store &&
        this.creationDate === other.creationDate;
  }

  setString(key: string, value: string | string[] | null): void {
    this.store[key] = value;
  }

  setNumber(key: string, value: number | null): void {
    this.store[key] = value;
  }

  setBoolean(key: string, value: boolean | null): void {
    this.store[key] = value;
  }

  getString(key: string): string | null {
    return MapUtils.getStringOrNull(this.store, key);
  }

  getStringArray(key: string): string[] | null {
    const rawValue = MapUtils.getRaw(this.store, key);

    if(rawValue != null && Array.isArray(rawValue)) {
      if(rawValue.length <= 0) {
        return [];
      }

      return typeof rawValue[0] === 'string' ? rawValue.map((e) => e as string) : null;
    }

    return null;
  }

  getNumber(key: string): number | null {
    return MapUtils.getNumberOrNull(this.store, key);
  }

  getBoolean(key: string): boolean | null {
    return MapUtils.getBooleanOrNull(this.store, key);
  }
}

export class KeyValueStoreBuilder extends DataModelBuilder<KeyValueStore> {
  build(context: SerializationContext, map: SerializedData): KeyValueStore {
    return KeyValueStore.fromMap(context, map);
  }
}
